import { Alert } from "@material-ui/lab";
import { MyDialog } from "../../../components/Dialog";
import { getTranslation } from "../../../heplers/translationHelper";
import { ReservationForm } from "../../public/component/ReservationForm";
import { AddAlert, NotificationImportant, NotificationsActive } from "@material-ui/icons";
import { ConfirmReservationDialog } from "../../public/component/ReservationForm/confirmReservationDialog";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useSnackbar } from "notistack";
import { Update_Reservation_URL } from "../../../constants/apiUrls";
import { Get, Post } from "../../../actions";
import { Box, Button, CircularProgress, Grid, TextField, Typography } from "@material-ui/core";
import { format } from "../../../heplers/format";
import { DatePicker } from "../../../components/DatePicker";
import ButtonWithLoading from "../../../components/ButtonWithLoading";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";

const parseReservedTables = (tableReserved) =>
  (tableReserved || "")
    .split("|")
    .map((item) => {
      const [id, reference] = item.split(",");
      return { id, reference };
    })
    .filter((t) => t.id);

const sameTableIds = (a, b) =>
  a.length === b.length && a.every((t) => b.some((x) => x.id === t.id));

const toDateKey = (d) => dayjs(d).format("YYYY-MM-DD");

const ALLERGIES_MAX_LENGTH = 100;
const EDIT_FORM_ID = "edit-reservation-form";

const EditReservationModal = ({
  openModal,
  onClose,
  isEdit,
  selectedReservation,
  params,
  setParams,
  refreshReservations
  
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const [newSelectedTable,setNewSelectedTable]=useState([]);
  const validationSchema = Yup.object().shape({
      persons: Yup.number().min(
        1,
        getTranslation(
          "Please select at least one person",
          "Please select at least one person",
          "Please select at least one person"
        )
      ),
      purposeOfReservation: Yup.number(),
    });

  const [editDate, setEditDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  // merge and submit live in the dialog header (outside the form), so their state is kept here
  const [isMerging, setIsMerging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [daySlots, setDaySlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const originalSlot = selectedReservation?.timeSlots?.[0] || null;
  const isPastReservation =
    !!selectedReservation?.reservationDate &&
    toDateKey(selectedReservation.reservationDate) < dayjs().format("YYYY-MM-DD");
  const originalTables = useMemo(
    () => parseReservedTables(selectedReservation?.tableReserved),
    [selectedReservation]
  );
  // tables are tied to a date and a service (midi/soir): moving the reservation releases them
  const sessionChanged =
    !!selectedReservation &&
    !!editDate &&
    (editDate !== toDateKey(selectedReservation.reservationDate) ||
      (!!selectedSlot && !!originalSlot && selectedSlot.dayTime !== originalSlot.dayTime));

  useEffect(() => {
    setIsMerging(false);
    setSaving(false);
    if (selectedReservation) {
      setNewSelectedTable(parseReservedTables(selectedReservation.tableReserved));
      setEditDate(toDateKey(selectedReservation.reservationDate));
      setSelectedSlot(selectedReservation.timeSlots?.[0] || null);
    } else {
      setNewSelectedTable([]);
      setEditDate(null);
      setSelectedSlot(null);
    }
  }, [selectedReservation]);

  // load all time slots of the selected weekday
  useEffect(() => {
    if (!editDate) return;
    setLoadingSlots(true);
    Get(
      { day: dayjs(editDate).day() },
      "Reservation/GetTimeSlots",
      null,
      (res) => {
        const toMinutes = (s) => dayjs(s).hour() * 60 + dayjs(s).minute();
        setDaySlots(
          [...(res?.data || [])].sort(
            (a, b) => toMinutes(a.startDateTime) - toMinutes(b.startDateTime)
          )
        );
        setLoadingSlots(false);
      },
      () => {
        setDaySlots([]);
        setLoadingSlots(false);
      }
    );
  }, [editDate]);

  const handleDateChange = (val) => {
    // a slot belongs to one weekday: pick again when the weekday changes
    if (selectedSlot && dayjs(val).day() !== dayjs(editDate).day())
      setSelectedSlot(null);
    setEditDate(val);
    setNewSelectedTable([]);
  };

  const handleSlotClick = (slot) => {
    if (selectedSlot && slot.dayTime !== selectedSlot.dayTime)
      setNewSelectedTable([]);
    setSelectedSlot(slot);
  };

  // data used by the table canvas, follows the edited date / slot
  const tableFormData = useMemo(() => {
    if (!selectedReservation || !selectedSlot) return null;
    return {
      ...selectedReservation,
      reservationDate: editDate,
      timeSlotId: selectedSlot.id,
      timeSlots: [selectedSlot],
      tableReserved: sessionChanged ? "" : selectedReservation.tableReserved,
    };
  }, [selectedReservation, editDate, selectedSlot, sessionChanged]);

  const showError = (err) =>
    enqueueSnackbar(
      typeof err?.data === "string" && err.data
        ? err.data
        : getTranslation(
            "Something went wrong. Please try again.",
            "Une erreur s'est produite. Veuillez réessayer.",
            "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut."
          ),
      { variant: "error" }
    );

  const addReservation = (payload, actions) => {
    const finish = () => {
      actions.setSubmitting(false);
      enqueueSnackbar(
        getTranslation(
          "Reservation updated successfully",
          "Réservation modifiée avec succès",
          "Reservierung erfolgreich aktualisiert"
        ),
        { variant: "success" }
      );
      if (refreshReservations) refreshReservations();
      onClose();
    };

    const tablesChanged = !sameTableIds(newSelectedTable, originalTables);
    // when the session changed the backend already released the old tables
    const assignTables =
      tablesChanged && (newSelectedTable.length > 0 || !sessionChanged);

    // update first (it may release tables), then assign tables, then refresh the list
    Post(
      payload,
      Update_Reservation_URL,
      null,
      () => {
        if (!assignTables) return finish();
        Post(
          { id: payload.id, tables: newSelectedTable.map((table) => ({ id: table.id })) },
          "Reservation/AssignTable",
          null,
          () => finish(),
          (err) => {
            actions.setSubmitting(false);
            showError(err);
            if (refreshReservations) refreshReservations();
          }
        );
      },
      (err) => {
        actions.setSubmitting(false);
        showError(err);
      }
    );
  };

  const isOriginalSlot = (slot) =>
    originalSlot?.id === slot.id &&
    editDate === toDateKey(selectedReservation?.reservationDate);

  const renderSlots = (dayTime, label) => {
    const slots = daySlots.filter((x) => x.dayTime === dayTime);
    if (slots.length === 0) return null;
    return (
      <Box mb={1}>
        <Typography variant="subtitle2">{label}</Typography>
        <Box display="flex" flexWrap="wrap" style={{ gap: 8 }} mt={0.5}>
          {slots.map((slot) => (
            <Button
              key={slot.id}
              size="small"
              variant={selectedSlot?.id === slot.id ? "contained" : "outlined"}
              color={selectedSlot?.id === slot.id ? "primary" : "default"}
              onClick={() => handleSlotClick(slot)}
              title={
                isOriginalSlot(slot)
                  ? getTranslation("Current time", "Heure actuelle", "Aktuelle Uhrzeit")
                  : undefined
              }
            >
              {format("time", new Date(slot.startDateTime))}
              {isOriginalSlot(slot) ? " •" : ""}
            </Button>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <MyDialog
      open={openModal}
      onClose={onClose}
      title={
        isEdit
          ? getTranslation(
              "Edit Reservation",
              "Edit Reservation",
              "Edit Reservation"
            )
          : getTranslation(
              "Add Reservation",
              "Add Reservation",
              "Add Reservation"
            )
      }
      headerCenter={
        <>
          {tableFormData && (
            <Button
              variant="contained"
              onClick={() => setIsMerging(!isMerging)}
              style={{
                backgroundColor: isMerging ? "#FFE0B2" : "#ffffff",
                color: "#004467",
                fontWeight: 600,
                minWidth: 180,
              }}
            >
              {isMerging
                ? getTranslation("Finish merging", "Terminer la fusion", "Zusammenlegen beenden")
                : getTranslation("Merge tables", "Fusionner des tables", "Tische zusammenlegen")}
            </Button>
          )}
          <ButtonWithLoading
            type="submit"
            form={EDIT_FORM_ID}
            loading={saving}
            title={getTranslation("Submit", "Submit", "Submit")}
            variant="contained"
            color="secondary"
            textColor="white"
            style={{ minWidth: 180, fontWeight: 600, color: "#fff" }}
          />
        </>
      }
      fullScreen
    >
      <div>
        <Alert
          variant="standard"
          color="warning"
          icon={<NotificationsActive />}
        >
          {" "}
          {getTranslation(
            "Adding reservation from admin side have no restrictions and no auto assigning of tables.",
            "Adding reservation from admin side have no restrictions and no auto assigning of tables.",
            "Adding reservation from admin side have no restrictions and no auto assigning of tables."
          )}{" "}
        </Alert>
        <Box mb={2} />
        <Formik
                    // enableReinitialize={loadingDaysOff}
                    validationSchema={validationSchema}
                    initialValues={selectedReservation}
                    onSubmit={(values, formikActions) => {
                      // keep the header submit button's spinner in step with Formik
                      const actions = {
                        ...formikActions,
                        setSubmitting: (value) => {
                          formikActions.setSubmitting(value);
                          setSaving(value);
                        },
                      };
                      actions.setSubmitting(true);

                        if (values.haveSmallKids && values.kids < 1) {
                          actions.setSubmitting(false);
                          enqueueSnackbar(
                            getTranslation(
                              "Minimum of 1 kid required",
                              "Minimum of 1 kid required",
                              "Minimum of 1 kid required"
                            ),
                            {
                              variant: "error",
                            }
                          );
                          return;
                        }
        
                        if (!selectedSlot) {
                          actions.setSubmitting(false);
                          enqueueSnackbar(
                            getTranslation(
                              "Please select a time slot",
                              "Veuillez sélectionner un créneau horaire",
                              "Bitte wählen Sie ein Zeitfenster"
                            ),
                            { variant: "error" }
                          );
                          return;
                        }

                        const payload = {
                          id: values.id,
                          allergies: values.allergies,
                          haveSmallKids: values.haveSmallKids,
                          kids: values.kids,
                          persons: values.persons,
                          timeSlotId: selectedSlot?.id,
                          timeSlots: selectedSlot ? [selectedSlot] : [],
                          maxCapacityId:values.maxCapacityId,
                          areaId:values.areaId,
                          // plain "YYYY-MM-DD": a dayjs object is sent as UTC and becomes the previous day
                          reservationDate: toDateKey(editDate),
                          purposeOfReservation: values.purposeOfReservation,
                          customer: {
                            fullName: values.fullName,
                            email: values.email,
                            phone: values.phone,
                            subscribeForNews: values.subscribeForNews,
                          },
                        };
                        addReservation(payload, actions);
                      
                    }}
                    validateOnChange={false}
                  >
                    {({
                      values,
                      setValues,
                      setFieldValue,
                      errors,
                      touched,
                      handleChange,
                      handleSubmit,
                      isSubmitting,
                    }) => {
        
        
                      return (
                        <Form id={EDIT_FORM_ID}>
                          <Box mb={2}>
                            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                              {getTranslation("Time", "Heure", "Uhrzeit")}
                            </Typography>
                            {loadingSlots ? (
                              <CircularProgress size={24} />
                            ) : daySlots.length === 0 ? (
                              <Typography color="error" variant="body2">
                                {getTranslation(
                                  "No time slots configured for this day.",
                                  "Aucun créneau horaire configuré pour ce jour.",
                                  "Für diesen Tag sind keine Zeitfenster konfiguriert."
                                )}
                              </Typography>
                            ) : (
                              <>
                                {renderSlots(0, getTranslation("Morning", "Midi", "Mittag"))}
                                {renderSlots(1, getTranslation("Evening", "Soir", "Abend"))}
                              </>
                            )}
                            {sessionChanged && originalTables.length > 0 && (
                              <Typography variant="body2" style={{ color: "#B00020", marginTop: 8 }}>
                                {getTranslation(
                                  "The previously assigned tables will be released. Please select tables again.",
                                  "Les tables attribuées seront libérées. Veuillez sélectionner à nouveau les tables.",
                                  "Die zugewiesenen Tische werden freigegeben. Bitte wählen Sie erneut Tische aus."
                                )}
                              </Typography>
                            )}
                          </Box>
                          {/* compact single row so the table plan is visible without scrolling */}
                          <Grid container spacing={2}>
                            <Grid item xs={6} md={2}>
                              <TextField

                                variant="outlined"
                                required
                                fullWidth
                                label={getTranslation(
                                  "Number of Persons",
                                  "Number of Persons",
                                  "Number of Persons"
                                )}
                                max={selectedReservation?.persons}
                                name="persons"
                                type="number"
                                size="small"
                                value={values.persons}
                                onBlur={(e) => {
                                  setFieldValue("persons", e.target.value);
                                  // if(isEdit){
                                  //   setFieldValue("timeSlotId",null)
                                  // }
                                  // if(!isEdit)
                                  setFieldValue("timeSlotId", null);
                                }}
                                onChange={(e) => {
                                  // if (
                                  //   e.target.value <=
                                  //   selectedReservation.persons
                                  // )
                                    setFieldValue("persons", e.target.value);
                                  // else
                                  //   enqueueSnackbar(
                                  //     "cannot increase the number of Person",
                                  //     { variant: "error" }
                                  //   );
                                }}
                                error={
                                  touched.persons && Boolean(errors.persons)
                                }
                                helperText={touched.persons && errors.persons}
                                InputProps={{ inputProps: { min: 1 } }}
                              />
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <DatePicker
                                // className={classes.datePicker}
                                required
                                size="medium"
                                label={getTranslation("Date", "Date", "Date")}
                                value={editDate || ""}
                                onChange={(val) => {
                                  if (val) handleDateChange(val);
                                }}
                                id="date"
                                name="reservationDate"
                                // a reservation already in the past must stay editable (tables, status...)
                                disablePast={!isPastReservation}
                                // shouldDisableDate={(day) => isOffDay(day)}
                                error={
                                  touched.reservationDate &&
                                  Boolean(errors.reservationDate)
                                }
                                helperText={
                                  touched.reservationDate &&
                                  errors.reservationDate
                                }
                                // onMonthChange={(date)=>{
                                //   const nextMonth=new Date();
                                //   nextMonth.setMonth(values.res)
                                //   getDaysOff({from:})}}
                              />
                            </Grid>
                            <Grid item xs={6} md={2}>
                              <TextField
                                variant="outlined"
                                required
                                fullWidth
                                label={getTranslation(
                                  "Number of Kids",
                                  "Number of Kids",
                                  "Number of Kids"
                                )}
                                name="kids"
                                type="number"
                                size="small"
                                value={values.kids}
                                onChange={handleChange}
                                error={touched.kids && Boolean(errors.kids)}
                                helperText={touched.kids && errors.kids}
                                InputProps={{
                                  inputProps: { min: 0, max: 100 },
                                }}
                                // style={{ marginTop: "1rem" }}
                              />
                            </Grid>
                            <Grid item xs={12} md={5}>
                              <TextField
                                // className={classes.input}
                                variant="outlined"
                                multiline
                                minRows={1}
                                maxRows={2}
                                fullWidth
                                inputProps={{ maxLength: ALLERGIES_MAX_LENGTH }}
                                label={getTranslation(
                                  "Give information of allergies and Intolerance",
                                  "Give information of allergies and Intolerance",
                                  "Give information of allergies and Intolerance"
                                )}
                                name="allergies"
                                type="number"
                                size="small"
                                value={values.allergies}
                                onChange={handleChange}
                                error={
                                  touched.allergies && Boolean(errors.allergies)
                                }
                                helperText={
                                  (touched.allergies && errors.allergies) ||
                                  `${(values.allergies || "").length}/${ALLERGIES_MAX_LENGTH}`
                                }
                              />
                            </Grid>
                          </Grid>
                          <Box mb={2} />
                          {tableFormData && (
                          <ConfirmReservationDialog
                            isMerging={isMerging}
                            setIsMerging={setIsMerging}
                            selectedReservation={tableFormData}
                            dayTime={{...params,dayTime:selectedSlot?.dayTime ?? params.selectedDayTime}}
                            onConfirmReservation={(tables) =>{
                              setNewSelectedTable(tables)}
                            }
                            selectedTables={newSelectedTable}
                            setSelectedTables={(items)=>{
                              setNewSelectedTable(items)}}
                          />
                          )}
                        
                        </Form>
                      );}}
                      
                      </Formik>
      
      </div>
    </MyDialog>
  );
};

export default EditReservationModal;
